import { useState, useMemo, useEffect } from 'react';

import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Droplets,
  Gauge,
  Calendar,
  MapPin,
  Navigation,
  Target,
  Loader2,
  AlertCircle,
  ChevronRight,
  Radio,
  Plus,
  Trash2,
} from 'lucide-react';
import { useLocation, calculateDistance, formatDistance, parseLocation } from '../hooks/useLocation';
import { toast } from 'sonner';

interface Well {
  id: string;
  name: string;
  location?: string;
  company?: string;
  lastEntry?: string;
  status?: string;
  wellStatus?: 'draft' | 'active';
  staticPressure?: number;
  oilProduction?: number;
  waterProduction?: number;
}

interface WellsListWithLocationProps {
  wells: Well[];
  onSelectWell: (well: Well) => void;
  onDeleteWell?: (well: Well) => void;
  onAddEntry?: (well: Well) => void;
  onAddWell?: () => void;
  onNavigateToWell?: (well: Well) => void;
  isDarkMode?: boolean;
}

interface WellWithDistance extends Well {
  distance?: number;
  distanceText?: string;
  coordinates?: { lat: number; lon: number };
}

export default function WellsListWithLocation({
  wells,
  onSelectWell,
  onDeleteWell,
  onAddEntry,
  onAddWell,
  onNavigateToWell,
  isDarkMode = false
}: WellsListWithLocationProps) {
  const location = useLocation();
  const [locationEnabled, setLocationEnabled] = useState(false);

  // Calculate distances and sort wells
  const wellsWithDistance = useMemo<WellWithDistance[]>(() => {
    if (!location.latitude || !location.longitude) {
      return wells.map(w => ({ ...w }));
    }

    const withDistances = wells.map(well => {
      const coords = parseLocation(well.location || '');

      if (coords) {
        const distance = calculateDistance(
          location.latitude!,
          location.longitude!,
          coords.lat,
          coords.lon
        );

        return {
          ...well,
          distance,
          distanceText: formatDistance(distance),
          coordinates: coords,
        };
      }

      return { ...well };
    });

    // Sort by distance (wells with coordinates first)
    return withDistances.sort((a, b) => {
      const aHasDistance = 'distance' in a && a.distance !== undefined;
      const bHasDistance = 'distance' in b && b.distance !== undefined;

      if (aHasDistance && bHasDistance) {
        return a.distance - b.distance;
      }
      if (aHasDistance) return -1;
      if (bHasDistance) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [wells, location.latitude, location.longitude]);

  // Find nearest well
  const nearestWell = useMemo(() => {
    if (!location.latitude || !location.longitude) return null;

    const wellsWithCoords = wellsWithDistance.filter((w): w is WellWithDistance & { distance: number } => w.distance !== undefined);
    if (wellsWithCoords.length === 0) return null;

    return wellsWithCoords[0];
  }, [wellsWithDistance, location.latitude, location.longitude]);

  // Check if user is "at" a well (within 500 feet / ~0.1 miles)
  const isAtWell = nearestWell && nearestWell.distance && nearestWell.distance < 0.1;

  useEffect(() => {
    if (locationEnabled && !location.isWatching) {
      location.watchLocation();
      toast.success('Location tracking enabled', {
        description: 'Your position is being tracked',
      });
    }
  }, [locationEnabled]);

  useEffect(() => {
    if (isAtWell && nearestWell) {
      toast.success(`You're at ${nearestWell.name}`, {
        description: `${nearestWell.distanceText} away`,
        duration: 3000,
      });
    }
  }, [isAtWell, nearestWell?.id]);

  const handleEnableLocation = async () => {
    setLocationEnabled(true);
    await location.requestLocation();
  };

  const handleDisableLocation = () => {
    setLocationEnabled(false);
    location.stopWatching();
    toast.info('Location tracking disabled');
  };

  const getStatusColor = (lastEntry?: string) => {
    if (!lastEntry) return 'bg-red-100 text-red-800';

    const daysSince = (Date.now() - new Date(lastEntry).getTime()) / (1000 * 60 * 60 * 24);

    if (daysSince > 14) return 'bg-red-100 text-red-800';
    if (daysSince > 7) return 'bg-orange-100 text-orange-800';
    if (daysSince > 4) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  const formatLastEntry = (lastEntry?: string) => {
    if (!lastEntry) return 'Never';
    const days = Math.floor((Date.now() - new Date(lastEntry).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days}d ago`;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Location Control Header */}
      <div className={`${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'} border-b p-4 space-y-3`}>
        {!locationEnabled ? (
          <Button
            onClick={handleEnableLocation}
            variant="outline"
            className={`w-full h-12 border-2 text-sm ${isDarkMode ? 'border-blue-700 text-blue-400 hover:bg-blue-900/20' : 'border-blue-500 text-blue-600 hover:bg-blue-50'}`}
            disabled={location.loading}
          >
            {location.loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Getting location...
              </>
            ) : (
              <>
                <Navigation className="h-4 w-4 mr-2" />
                Enable GPS for Nearest Wells
              </>
            )}
          </Button>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Radio className={`h-5 w-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  {location.isWatching && (
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                    </span>
                  )}
                </div>
                <div>
                  <p className={`text-sm font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Location Active</p>
                  {location.accuracy && (
                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Accuracy: ±{Math.round(location.accuracy * 3.28084)} ft
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={handleDisableLocation}
                variant="ghost"
                size="sm"
                className={isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-slate-800' : 'text-gray-600'}
              >
                Disable
              </Button>
            </div>

            {nearestWell && (
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Nearest: {nearestWell.name} ({nearestWell.distanceText})
              </div>
            )}
          </div>
        )}

        {location.error && (
          <div className={`${isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'} border rounded-lg p-3`}>
            <div className="flex items-start space-x-2">
              <AlertCircle className={`h-4 w-4 ${isDarkMode ? 'text-red-400' : 'text-red-600'} flex-shrink-0 mt-0.5`} />
              <p className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-800'}`}>{location.error}</p>
            </div>
          </div>
        )}
      </div>

      {/* Wells List */}
      <div className={`flex-1 overflow-y-auto ${isDarkMode ? 'bg-slate-950' : 'bg-gray-50'} p-4`}>
        {/* Add New Well Button */}
        {onAddWell && (
          <Button
            onClick={onAddWell}
            className={`w-full h-14 mb-4 rounded-2xl shadow-lg border transition-all duration-300 ${isDarkMode ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white border-green-500/30 shadow-green-900/20' : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white border-green-400/20'}`}
          >
            <Plus className="h-5 w-5 mr-2" />
            <span className="font-semibold text-lg">Add New Well</span>
          </Button>
        )}

        {wellsWithDistance.length === 0 ? (
          <div className={`rounded-3xl border border-dashed p-8 text-center ${isDarkMode ? 'bg-slate-900/50 border-slate-800' : 'bg-white border-slate-200'}`}>
            <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
              <Droplets className={`h-8 w-8 ${isDarkMode ? 'text-slate-600' : 'text-slate-400'}`} />
            </div>
            <h3 className={`text-lg font-semibold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>No wells found</h3>
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Get started by adding a well to your route.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {wellsWithDistance.map((well) => {
              const isNearest = nearestWell?.id === well.id;
              const isVeryClose = isNearest && isAtWell;

              // Dynamic gradients based on status
              const containerGradient = isVeryClose
                ? 'from-green-500 via-emerald-500 to-teal-500'
                : isNearest
                  ? 'from-blue-500 via-cyan-500 to-sky-500'
                  : isDarkMode
                    ? 'from-slate-800 to-slate-900'
                    : 'from-white to-gray-50';

              const iconBg = isVeryClose
                ? 'bg-gradient-to-br from-green-500 to-emerald-600'
                : isNearest
                  ? 'bg-gradient-to-br from-blue-500 to-cyan-600'
                  : isDarkMode
                    ? 'bg-slate-800'
                    : 'bg-slate-100';

              const iconColor = isVeryClose || isNearest
                ? 'text-white'
                : isDarkMode
                  ? 'text-slate-400'
                  : 'text-slate-500';

              return (
                <div
                  key={well.id}
                  onClick={() => onSelectWell(well)}
                  className={`
                    relative group rounded-[1.5rem] p-[1px] transition-all duration-300
                    bg-gradient-to-br ${containerGradient}
                    ${isVeryClose ? 'shadow-lg shadow-green-500/20' : ''}
                    ${isNearest && !isVeryClose ? 'shadow-lg shadow-blue-500/20' : ''}
                    hover:scale-[1.01] hover:shadow-xl cursor-pointer
                  `}
                >
                  <div className={`
                    rounded-[1.4rem] h-full p-4 relative overflow-hidden
                    ${isDarkMode ? 'bg-slate-950/90' : 'bg-white/95'}
                    backdrop-blur-xl
                  `}>
                    {/* Delete Button */}
                    {onDeleteWell && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteWell(well);
                        }}
                        className={`
                          absolute top-4 right-4 z-10 p-2 rounded-xl transition-all duration-200
                          opacity-0 group-hover:opacity-100
                          ${isDarkMode
                            ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                            : 'bg-red-50 text-red-500 hover:bg-red-100'
                          }
                        `}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}

                    <div className="flex items-start gap-4">
                      {/* Icon Avatar */}
                      <div className={`
                        w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm
                        ${iconBg}
                      `}>
                        {isVeryClose ? (
                          <Target className={`h-6 w-6 ${iconColor} animate-pulse`} />
                        ) : isNearest ? (
                          <Navigation className={`h-6 w-6 ${iconColor}`} />
                        ) : (
                          <Droplets className={`h-6 w-6 ${iconColor}`} />
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between pr-8">
                          <div>
                            <h3 className={`font-bold text-lg truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {well.name}
                            </h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              {well.location && (
                                <div className={`flex items-center text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                  <MapPin className="h-3 w-3 mr-1" />
                                  <span className="truncate max-w-[150px]">{well.location}</span>
                                </div>
                              )}
                              {well.distanceText && (
                                <Badge variant="secondary" className={`
                                  h-5 text-[10px] px-1.5 font-bold tracking-wide
                                  ${isVeryClose
                                    ? 'bg-green-500/20 text-green-600 dark:text-green-400 dark:bg-green-500/10'
                                    : isNearest
                                      ? 'bg-blue-500/20 text-blue-600 dark:text-blue-400 dark:bg-blue-500/10'
                                      : isDarkMode ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
                                  }
                                `}>
                                  {well.distanceText}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 gap-3 mt-4">
                          <div className={`
                            rounded-xl p-2.5 flex items-center justify-between
                            ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50'}
                          `}>
                            <div className="flex items-center gap-2">
                              <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-white shadow-sm'}`}>
                                <Droplets className="h-3.5 w-3.5 text-blue-500" />
                              </div>
                              <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Oil</span>
                            </div>
                            <span className={`text-sm font-bold font-mono ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {(well.oilProduction || 0).toFixed(1)}
                            </span>
                          </div>

                          <div className={`
                            rounded-xl p-2.5 flex items-center justify-between
                            ${isDarkMode ? 'bg-slate-800/50' : 'bg-slate-50'}
                          `}>
                            <div className="flex items-center gap-2">
                              <div className={`p-1.5 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-white shadow-sm'}`}>
                                <Gauge className="h-3.5 w-3.5 text-purple-500" />
                              </div>
                              <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Gas</span>
                            </div>
                            <span className={`text-sm font-bold font-mono ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {(well.staticPressure || 0).toFixed(0)}
                            </span>
                          </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-dashed border-slate-200 dark:border-slate-800">
                          <div className="flex items-center gap-1.5">
                            <Calendar className={`h-3.5 w-3.5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`} />
                            <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                              {formatLastEntry(well.lastEntry)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            {isVeryClose && onAddEntry && (
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddEntry(well);
                                }}
                                className="h-8 bg-green-600 hover:bg-green-700 text-white rounded-lg shadow-sm shadow-green-900/20"
                              >
                                <Plus className="h-3.5 w-3.5 mr-1.5" /> Entry
                              </Button>
                            )}

                            {onNavigateToWell && well.coordinates && !isVeryClose && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onNavigateToWell(well);
                                }}
                                className={`h-8 px-2 ${isDarkMode ? 'text-blue-400 hover:bg-blue-900/20' : 'text-blue-600 hover:bg-blue-50'}`}
                              >
                                <Navigation className="h-3.5 w-3.5 mr-1.5" /> Navigate
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
